"use client";

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../../components/IntranetHeader";
import { supabase } from "../../../supabaseClient";

function formatRoleplayNhsNumber(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length !== 10) return digits || "Not assigned";
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

export default function Lookup() {
  const [query, setQuery] = useState("");
  const [patients, setPatients] = useState([]);
  const [portalOnlyAccounts, setPortalOnlyAccounts] = useState([]);
  const [matchedPortalAccounts, setMatchedPortalAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const triageColors = {
    A: "bg-red-600 text-white",
    B: "bg-orange-500 text-white",
    C: "bg-yellow-400 text-black",
    D: "bg-green-500 text-white",
    E: "bg-blue-500 text-white",
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const date = new Date(timeString);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    const searchValue = query.trim();
    if (!searchValue) return;

    setLoading(true);
    setError(null);
    setSearched(true);
    setPatients([]);
    setPortalOnlyAccounts([]);
    setMatchedPortalAccounts([]);

    try {
      const digits = searchValue.replace(/\D/g, "");
      const isRoleplayNhsNumber = digits.length === 10;

      let patientRows = [];
      let portalRows = [];

      if (isRoleplayNhsNumber) {
        const [patientResult, portalResult] = await Promise.all([
          supabase
            .from("patients")
            .select("*")
            .eq("roleplay_nhs_number", digits)
            .order("arrival_time", { ascending: false }),
          supabase
            .from("patient_portal_accounts")
            .select(
              "id, roleplay_nhs_number, roleplay_name, roblox_username, roblox_display_name, date_of_birth, created_at",
            )
            .eq("roleplay_nhs_number", digits)
            .order("created_at", { ascending: false }),
        ]);

        if (patientResult.error) throw patientResult.error;
        if (portalResult.error) throw portalResult.error;

        patientRows = patientResult.data || [];
        portalRows = portalResult.data || [];
      } else {
        const pattern = `%${searchValue}%`;

        const [
          patientRoleplayResult,
          patientRobloxResult,
          patientNameResult,
          portalRoleplayResult,
          portalRobloxResult,
        ] = await Promise.all([
          supabase
            .from("patients")
            .select("*")
            .ilike("roleplay_name", pattern)
            .order("arrival_time", { ascending: false }),
          supabase
            .from("patients")
            .select("*")
            .ilike("roblox_username", pattern)
            .order("arrival_time", { ascending: false }),
          supabase
            .from("patients")
            .select("*")
            .ilike("full_name", pattern)
            .order("arrival_time", { ascending: false }),
          supabase
            .from("patient_portal_accounts")
            .select(
              "id, roleplay_nhs_number, roleplay_name, roblox_username, roblox_display_name, date_of_birth, created_at",
            )
            .ilike("roleplay_name", pattern)
            .order("created_at", { ascending: false }),
          supabase
            .from("patient_portal_accounts")
            .select(
              "id, roleplay_nhs_number, roleplay_name, roblox_username, roblox_display_name, date_of_birth, created_at",
            )
            .ilike("roblox_username", pattern)
            .order("created_at", { ascending: false }),
        ]);

        const results = [
          patientRoleplayResult,
          patientRobloxResult,
          patientNameResult,
          portalRoleplayResult,
          portalRobloxResult,
        ];

        const firstError = results.find((result) => result.error)?.error;
        if (firstError) throw firstError;

        const patientMap = new Map();
        [
          ...(patientRoleplayResult.data || []),
          ...(patientRobloxResult.data || []),
          ...(patientNameResult.data || []),
        ].forEach((patient) => {
          if (patient?.id) patientMap.set(patient.id, patient);
        });

        const portalMap = new Map();
        [
          ...(portalRoleplayResult.data || []),
          ...(portalRobloxResult.data || []),
        ].forEach((account) => {
          if (account?.id) portalMap.set(account.id, account);
        });

        patientRows = Array.from(patientMap.values());
        portalRows = Array.from(portalMap.values());
      }

      setMatchedPortalAccounts(portalRows);

      const linkedPortalIds = new Set(
        patientRows.map((patient) => patient.portal_account_id).filter(Boolean),
      );
      const linkedNhsNumbers = new Set(
        patientRows
          .map((patient) =>
            String(patient.roleplay_nhs_number || "").replace(/\D/g, ""),
          )
          .filter(Boolean),
      );
      const linkedRobloxNames = new Set(
        patientRows
          .map((patient) => patient.roblox_username?.trim().toLowerCase())
          .filter(Boolean),
      );

      const portalOnly = portalRows.filter((account) => {
        if (linkedPortalIds.has(account.id)) return false;

        const accountNhs = String(account.roleplay_nhs_number || "").replace(
          /\D/g,
          "",
        );
        const accountRoblox = account.roblox_username?.trim().toLowerCase();

        if (accountNhs && linkedNhsNumbers.has(accountNhs)) return false;
        if (accountRoblox && linkedRobloxNames.has(accountRoblox)) return false;
        return true;
      });

      setPatients(patientRows);
      setPortalOnlyAccounts(portalOnly);
    } catch (searchError) {
      console.error(searchError);
      setError("Unable to search patient records.");
    } finally {
      setLoading(false);
    }
  };

  const findPortalAccountForPatient = (patient) => {
    if (patient.portal_account_id) return patient.portal_account_id;

    const nhsNumber = String(patient.roleplay_nhs_number || "").replace(/\D/g, "");
    const robloxName = patient.roblox_username?.trim().toLowerCase();

    const match = matchedPortalAccounts.find((account) => {
      const accountNhs = String(account.roleplay_nhs_number || "").replace(/\D/g, "");
      const accountRoblox = account.roblox_username?.trim().toLowerCase();
      return (nhsNumber && accountNhs === nhsNumber) ||
        (robloxName && accountRoblox === robloxName);
    });

    return match?.id || null;
  };

  const resultCount = patients.length + portalOnlyAccounts.length;

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <div className="nhsuk-width-container mt-5">
        <main className="nhsuk-main-wrapper" id="maincontent">
          <div className="nhsuk-grid-row">
            <div className="nhsuk-grid-column-full">
              <h1 className="nhsuk-heading-l">Patient lookup service</h1>

              <p className="nhsuk-body">
                Search using a roleplay name, Roblox username or a 10-digit RUHT
                roleplay NHS number.
              </p>

              <form onSubmit={handleSearch} className="nhsuk-form-group">
                <div className="nhsuk-form-group nhsuk-u-margin-bottom-4">
                  <label className="nhsuk-label" htmlFor="patient-search">
                    Roleplay name, Roblox username or roleplay NHS number
                  </label>

                  <div className="nhsuk-hint" id="patient-search-hint">
                    Roleplay names and Roblox usernames can be entered partially.
                    Roleplay NHS numbers can be entered with or without spaces.
                  </div>

                  <input
                    className="nhsuk-input nhsuk-input--width-20"
                    id="patient-search"
                    name="patient-search"
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="e.g. Gus Farnsworth, CJayStreamz07 or 123 456 7890"
                    aria-describedby="patient-search-hint"
                  />
                </div>

                <button className="nhsuk-button" type="submit" disabled={loading}>
                  {loading ? "Searching…" : "Search"}
                </button>

                <button
                  type="button"
                  className="nhsuk-button nhsuk-button--secondary ml-3"
                  onClick={() => navigate("/intranet/wards")}
                >
                  View wards
                </button>
              </form>

              {error && (
                <div className="nhsuk-error-summary mt-4" role="alert">
                  <h2 className="nhsuk-error-summary__title">
                    There is a problem
                  </h2>
                  <div className="nhsuk-error-summary__body">
                    <p>{error}</p>
                  </div>
                </div>
              )}

              {searched && !loading && !error && (
                <>
                  <h2 className="nhsuk-heading-m mt-6">Search results</h2>

                  <p className="nhsuk-body">
                    {resultCount} result{resultCount !== 1 ? "s" : ""} found
                  </p>

                  {patients.length > 0 && (
                    <div className="overflow-x-auto w-full">
                      <table className="nhsuk-table w-full border-collapse">
                        <thead className="nhsuk-table__head">
                          <tr className="nhsuk-table__row border-b-[3px] border-black">
                            <th className="nhsuk-table__header">Name</th>
                            <th className="nhsuk-table__header">
                              Roleplay NHS number
                            </th>
                            <th className="nhsuk-table__header">Roblox</th>
                            <th className="nhsuk-table__header text-center">
                              Triage
                            </th>
                            <th className="nhsuk-table__header">
                              Presenting complaint
                            </th>
                            <th className="nhsuk-table__header">Age</th>
                            <th className="nhsuk-table__header">Arrival</th>
                            <th className="nhsuk-table__header">Location</th>
                            <th className="nhsuk-table__header">Bed</th>
                            <th className="nhsuk-table__header">Actions</th>
                          </tr>
                        </thead>

                        <tbody className="nhsuk-table__body">
                          {patients.map((patient) => (
                            <tr
                              key={patient.id}
                              className="nhsuk-table__row border-b border-black"
                            >
                              <td className="nhsuk-table__cell">
                                <strong>{patient.full_name}</strong>
                              </td>

                              <td className="nhsuk-table__cell">
                                <strong>
                                  {formatRoleplayNhsNumber(
                                    patient.roleplay_nhs_number,
                                  )}
                                </strong>
                              </td>

                              <td className="nhsuk-table__cell">
                                {patient.roblox_username ? (
                                  <span>@{patient.roblox_username}</span>
                                ) : (
                                  <span className="text-gray-400">
                                    Not recorded
                                  </span>
                                )}
                              </td>

                              <td className="nhsuk-table__cell text-center">
                                {patient.triage_band && (
                                  <span
                                    className={`inline-block ${
                                      triageColors[patient.triage_band]
                                    } text-xs px-1.5 py-0.5 rounded-full font-bold`}
                                  >
                                    {patient.triage_band}
                                  </span>
                                )}
                              </td>

                              <td className="nhsuk-table__cell">
                                {patient.presenting_complaint}
                              </td>
                              <td className="nhsuk-table__cell">{patient.age}</td>
                              <td className="nhsuk-table__cell">
                                {formatTime(patient.arrival_time)}
                              </td>
                              <td className="nhsuk-table__cell">
                                {patient.location}
                              </td>
                              <td className="nhsuk-table__cell">
                                {patient.bed || "N/A"}
                              </td>
                              <td className="nhsuk-table__cell">
                                <div className="pguh-patient-lookup-actions">
                                  {findPortalAccountForPatient(patient) && (
                                    <Link
                                      to={`/intranet/ecamis/patient-overview/${findPortalAccountForPatient(patient)}`}
                                      className="nhsuk-link"
                                    >
                                      Patient overview
                                    </Link>
                                  )}
                                  <Link
                                    to={`/intranet/ecamis/patient/${patient.id}`}
                                    className="nhsuk-link"
                                  >
                                    Open clinical record
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {portalOnlyAccounts.length > 0 && (
                    <section className="nhsuk-card mt-5">
                      <div className="nhsuk-card__content">
                        <h3 className="nhsuk-heading-m">
                          Portal accounts without a patient record
                        </h3>
                        <p className="nhsuk-body">
                          These accounts match the search. You can open the patient
                          overview to view their patient-provided information,
                          appointments and any linked hospital visit history.
                        </p>

                        <div className="overflow-x-auto w-full">
                          <table className="nhsuk-table w-full">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Roleplay NHS number</th>
                                <th>Roblox</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {portalOnlyAccounts.map((account) => (
                                <tr key={account.id}>
                                  <td>{account.roleplay_name}</td>
                                  <td>
                                    <strong>
                                      {formatRoleplayNhsNumber(
                                        account.roleplay_nhs_number,
                                      )}
                                    </strong>
                                  </td>
                                  <td>@{account.roblox_username}</td>
                                  <td>Portal account only</td>
                                  <td>
                                    <Link
                                      className="nhsuk-link"
                                      to={`/intranet/ecamis/patient-overview/${account.id}`}
                                    >
                                      View patient overview
                                    </Link>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </section>
                  )}

                  {resultCount === 0 && (
                    <div className="nhsuk-inset-text">
                      No patient or portal account matched that Roblox username
                      or roleplay NHS number.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
